"use client";

import { Separator } from "@/components/ui/separator";
import type { ClassType } from "@/lib/tailwind/properties";
import { cn } from "@/lib/utils";
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ComboBox,
  type ComboBoxOptionType,
  DropdownInput,
  type DropdownOptionType,
  type TooltipProps,
} from ".";
import ColorSwatch from "./ColorSwatch";
import type {
  GradientState,
  GradientInputState,
} from "../../hooks/useGradient";
import { hexToRgba } from "./color-picker/utils/convert";
import {
  parseColorValue,
  cleanGradientVariables,
  safeExtractValueFromCss,
  cleanColorValue,
  extractPositionValue,
} from "./color-picker/utils/colorFormatUtils";
import { ChevronDown } from "lucide-react";
import { ResetIcon } from "@radix-ui/react-icons";
import type { NewPropertyState } from "@/components/RightStyle/usePropertyStateV2";
import { VariantDraggableInput as DraggableInput } from "./VariantDraggableInput";
import PROPERTIES from "@/lib/tailwind/properties";
import { useNewPropertyState2 } from "@/components/RightStyle/usePropertyStateV2";
import { PropertyType } from "@/tailwind/tailwindUtils";

interface CustomGradientInputProps {
  backgroundImageState: NewPropertyState;
  backgroundImageProperties: ClassType[];
  backgroundColorProperties: ClassType[];
  gradientDirectionIcon: React.JSX.Element;
  setValue: (option: ComboBoxOptionType) => void;
  removeTailwindClass: (className: string) => void;
  classList: string[];
  instantUpdateValue: (option: ComboBoxOptionType) => void;
  fromColorProperties?: ComboBoxOptionType[];
  toColorProperties?: ComboBoxOptionType[];
  viaColorProperties?: ComboBoxOptionType[];
  fromPositionProperties: ClassType[];
  toPositionProperties: ClassType[];
  viaPositionProperties: ClassType[];
  set: boolean;
  setSet: (set: boolean) => void;
  onClear: () => void;
  evaluatedValue?: string;
  gradient: GradientState;
  setGradient: (val: React.SetStateAction<GradientState>) => void;
  buildArbitraryClass: (
    currentColor: "from" | "to" | "via",
    input: string
  ) => ComboBoxOptionType;
  onOpenFromVariantModal?: () => void;
  fromVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenViaVariantModal?: () => void;
  viaVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenToVariantModal?: () => void;
  toVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  pickTailwindClassForProperty: (args: {
    option: ComboBoxOptionType;
    propertyType: string;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  fromPositionState?: any;
  viaPositionState?: any;
  toPositionState?: any;
}

const CustomGradientInput: React.FC<
  CustomGradientInputProps & TooltipProps
> = ({
  backgroundImageState,
  backgroundImageProperties,
  gradientDirectionIcon,
  fromColorProperties,
  toColorProperties,
  viaColorProperties,
  instantUpdateValue,
  fromPositionProperties,
  toPositionProperties,
  viaPositionProperties,
  removeTailwindClass,
  classList,
  setValue,
  gradient,
  setGradient,
  buildArbitraryClass,
  onOpenFromVariantModal,
  fromVariantAnchorRef,
  onOpenViaVariantModal,
  viaVariantAnchorRef,
  onOpenToVariantModal,
  toVariantAnchorRef,
  pickTailwindClassForProperty,
  fromPositionState,
  viaPositionState,
  toPositionState,
}) => {
  const [localGradient, setLocalGradient] = useState<GradientState>(gradient);
  const [currentColor, setCurrentColor] = useState<"from" | "to" | "via">(
    "from"
  );

  const fromColorValue = useMemo(() => {
    if (localGradient.from?.color?.value) {
      const cleanedValue = cleanGradientVariables(
        localGradient.from.color.value
      );
      const parsedColor = parseColorValue(cleanedValue);
      return parsedColor.hex;
    }

    return "#ffffff";
  }, [gradient, localGradient, currentColor]);

  const toColorValue = useMemo(() => {
    if (localGradient.to?.color?.value) {
      const cleanedValue = cleanGradientVariables(localGradient.to.color.value);
      const parsedColor = parseColorValue(cleanedValue);
      return parsedColor.hex;
    }

    return "#000000";
  }, [gradient, localGradient, currentColor]);

  const viaColorValue = useMemo(() => {
    if (localGradient.via?.color?.value) {
      const cleanedValue = cleanGradientVariables(
        localGradient.via.color.value
      );
      const parsedColor = parseColorValue(cleanedValue);
      return parsedColor.hex;
    }

    return "#000000";
  }, [gradient, localGradient, currentColor]);

  const handleUpdateGradientSet = useCallback(
    (value: GradientState) => {
      setLocalGradient(value);
      setGradient(value);
    },
    [gradient, localGradient]
  );

  useEffect(() => {
    if (localGradient == null || localGradient.direction == null) {
      setLocalGradient(gradient);
    }
  }, [gradient]);

  const fromColorState = useNewPropertyState2({
    elementClasses: classList,
    tailwindClassMap: {},
    propertyType: PropertyType.FROM_COLOR,
    cssValues: {},
  });

  return (
    <div className="col-span-14">
      <div className="grid grid-cols-14 items-center gap-x-3 gap-y-2 mt-2">
        <div className="col-span-5">
          <div className="text-xs text-secondary-800 font-medium">
            Direction
          </div>
        </div>
        <div className="col-span-9">
          <DraggableInput
            propertyState={{
              ...backgroundImageState,
              propertyValue: backgroundImageState.propertyValue?.includes(
                "url("
              )
                ? "to right"
                : backgroundImageState.propertyValue,
              displayValue: backgroundImageState.displayValue?.includes("url(")
                ? "to right"
                : backgroundImageState.displayValue,
              valueByVariant: backgroundImageState.valueByVariant || {},
            }}
            labelContent={gradientDirectionIcon}
            comboboxOptions={backgroundImageProperties.filter(
              (option) =>
                option.tailwind.startsWith("bg-gradient-") ||
                option.tailwind === "bg-none"
            )}
            onComboboxSelected={(option, variant) => {
              if (!option.tailwind.startsWith("bg-[url(")) {
                pickTailwindClassForProperty({
                  option,
                  propertyType: "BACKGROUND_IMAGE",
                  setTemporaryDraggingValue:
                    backgroundImageState.setTemporaryDraggingValue,
                  variant,
                });
              }
            }}
            onSliderValueUpdate={(option, variant) => {
              pickTailwindClassForProperty({
                option,
                propertyType: "BACKGROUND_IMAGE",
                setTemporaryDraggingValue:
                  backgroundImageState.setTemporaryDraggingValue,
                variant,
              });
            }}
            tooltipContent="Gradient direction"
            onClear={() => {
              const imageClass = classList.find((c) =>
                c.startsWith("bg-[url(")
              );
              const gradientClass = classList.find((c) =>
                c.startsWith("bg-gradient-")
              );

              const toRemove = imageClass || gradientClass;

              if (toRemove) {
                removeTailwindClass(toRemove);
              }
            }}
            draggable={false}
          />
        </div>
        <div className="col-span-14">
          <Separator className="bg-secondary-n-900" />
        </div>
        <GradientInputMemo
          title="From"
          value={localGradient.from}
          setValue={setValue}
          instantUpdateValue={instantUpdateValue}
          colorProperties={fromColorProperties}
          removeTailwindClass={removeTailwindClass}
          buildArbitraryClass={(input) => buildArbitraryClass("from", input)}
          setCurrentColor={() => setCurrentColor("from")}
          setGradient={handleUpdateGradientSet}
          positionProperties={fromPositionProperties}
          dropdownOptions={[]}
          onOpenVariantModal={onOpenFromVariantModal}
          variantAnchorRef={fromVariantAnchorRef}
          pickTailwindClassForProperty={pickTailwindClassForProperty}
          positionState={fromPositionState}
          fromColorValue={fromColorValue}
          toColorValue={toColorValue}
          viaColorValue={viaColorValue}
          classList={classList}
        />
        <div className="col-span-14">
          <Separator className="bg-secondary-n-900" />
        </div>
        <GradientInputMemo
          title="Via"
          value={localGradient.via}
          setValue={setValue}
          instantUpdateValue={instantUpdateValue}
          colorProperties={viaColorProperties}
          removeTailwindClass={removeTailwindClass}
          buildArbitraryClass={(input) => buildArbitraryClass("via", input)}
          setCurrentColor={() => setCurrentColor("via")}
          setGradient={handleUpdateGradientSet}
          positionProperties={viaPositionProperties}
          dropdownOptions={[]}
          onOpenVariantModal={onOpenViaVariantModal}
          variantAnchorRef={viaVariantAnchorRef}
          pickTailwindClassForProperty={pickTailwindClassForProperty}
          positionState={viaPositionState}
          fromColorValue={fromColorValue}
          toColorValue={toColorValue}
          viaColorValue={viaColorValue}
          classList={classList}
        />
        <div className="col-span-14">
          <Separator className="bg-secondary-n-900" />
        </div>
        <GradientInputMemo
          title="To"
          value={localGradient.to}
          setValue={setValue}
          instantUpdateValue={instantUpdateValue}
          colorProperties={toColorProperties}
          removeTailwindClass={removeTailwindClass}
          buildArbitraryClass={(input) => buildArbitraryClass("to", input)}
          setCurrentColor={() => setCurrentColor("to")}
          setGradient={handleUpdateGradientSet}
          dropdownOptions={[]}
          positionProperties={toPositionProperties}
          onOpenVariantModal={onOpenToVariantModal}
          variantAnchorRef={toVariantAnchorRef}
          pickTailwindClassForProperty={pickTailwindClassForProperty}
          positionState={toPositionState}
          fromColorValue={fromColorValue}
          toColorValue={toColorValue}
          viaColorValue={viaColorValue}
          classList={classList}
        />
      </div>
    </div>
  );
};

const GradientInput = ({
  title,
  value,
  instantUpdateValue,
  colorProperties,
  setValue,
  removeTailwindClass,
  buildArbitraryClass,
  disabled = false,
  onInputFocus,
  dropdownOptions,
  setGradient,
  positionProperties,
  onOpenVariantModal,
  variantAnchorRef,
  pickTailwindClassForProperty,
  positionState,
  fromColorValue,
  toColorValue,
  viaColorValue,
  classList,
}: {
  title: string;
  value?: GradientInputState;
  instantUpdateValue: (option: ComboBoxOptionType) => void;
  setValue: (option: ComboBoxOptionType) => void;
  colorProperties?: ComboBoxOptionType[];
  removeTailwindClass: (className: string) => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  disabled?: boolean;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  setCurrentColor: () => void;
  dropdownOptions: DropdownOptionType[];
  setGradient: (val: React.SetStateAction<GradientState>) => void;
  positionProperties: ClassType[];
  onOpenVariantModal?: () => void;
  variantAnchorRef?: React.RefObject<HTMLButtonElement>;
  pickTailwindClassForProperty: (args: {
    option: ComboBoxOptionType;
    propertyType: string;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  positionState?: any;
  fromColorValue?: string;
  toColorValue?: string;
  viaColorValue?: string;
  classList: string[];
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [comboboxOpen, setComboboxOpen] = useState<null | boolean>(false);
  const [focused, setFocused] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState<null | boolean>(false);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [colorValue, setColorValue] = useState<string | undefined>(
    value?.color?.value
  );

  const lastSelectedColorRef = useRef<string | undefined>(value?.color?.value);

  const cleanedColorValue = useMemo(() => {
    return cleanColorValue(value?.color?.value, colorValue);
  }, [colorValue, value]);

  // Always derive the displayed value from props
  const positionValueForInput = useMemo(() => {
    if (value?.position?.value != undefined) {
      return Number(value?.position?.value.replace("%", ""));
    }
    return 0;
  }, [value, setValue]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    if (
      value?.color?.value === "" &&
      value?.color?.classname &&
      removeTailwindClass
    ) {
      setComboboxOpen(false);
      removeTailwindClass(value.color.classname);

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleDropdownOptionClick = (
    option: DropdownOptionType,
    event?: React.MouseEvent
  ) => {
    if (event) event.stopPropagation();
    const key = title.toLowerCase();
    if (option.value === "clear") {
      if (value?.color?.classname && removeTailwindClass) {
        removeTailwindClass(value.color.classname);
      }
      setGradient((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          position: {
            value: "",
            classname: "",
          },
        },
        direction: prev.direction,
      }));
    } else if (option.value !== value?.position?.value) {
      setValue({
        tailwind: `${key}-[${option.value}]`,
        css: `--tw-gradient-${key}-position: ${option.value}`,
      });
      setGradient((prev) => {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            position: {
              value: option.value,
              classname: `${key}-[${option.value}]`,
            },
            set: true,
          },
          direction: prev.direction,
        };
      });
    }
    setDropdownVisible(false);
  };

  const enhancedDropdownOptions = useMemo(() => {
    if (value?.set == true) {
      const clearDropdownOption: DropdownOptionType = {
        label: "Clear style",
        value: "clear",
        icon: <ResetIcon />,
      };

      // Return a new array with the 'Clear' option appended
      return [...dropdownOptions, clearDropdownOption];
    }

    return dropdownOptions;
  }, [dropdownOptions, value, removeTailwindClass]);

  const handleComboboxOptionClick = useCallback(
    (option: ComboBoxOptionType, event?: React.MouseEvent) => {
      if (event) event.stopPropagation();
      const key = title.toLowerCase();
      const newValue = safeExtractValueFromCss(option.css);
      if (newValue && newValue !== lastSelectedColorRef.current) {
        setValue(option);
        setComboboxOpen(false);
        setColorValue(newValue);
        setGradient((prev) => {
          return {
            ...prev,
            [key]: {
              ...prev[key],
              color: {
                value: newValue,
                classname: `${key}-[${newValue}]`,
              },
              set: true,
            },
            direction: prev.direction,
          };
        });
        lastSelectedColorRef.current = newValue;
      } else {
        setComboboxOpen(false);
      }
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
    [value]
  );

  const onInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const input = ev.target.value || "";
      const key = title.toLowerCase();
      if (input.length > 0) {
        setColorValue(input);
        setGradient((prev) => {
          return {
            ...prev,
            [key]: {
              ...prev[key],
              color: {
                value: input,
                classname: `${key}-[${input}]`,
              },
              set: true,
            },
            direction: prev.direction,
          };
        });

        if (buildArbitraryClass && instantUpdateValue) {
          instantUpdateValue(buildArbitraryClass(input));
        }
      }
      setComboboxOpen(true);
    },
    [value, buildArbitraryClass, instantUpdateValue]
  );

  const handlePositionRangeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const position = Math.round(parseFloat(e.target.value) * 100);
      const value = `${position}%`;
      const key = title.toLowerCase();
      if (isDragging) {
        let stopClass = `${key}-[${value}]`;
        let colorValue =
          key === "from"
            ? fromColorValue
            : key === "to"
            ? toColorValue
            : viaColorValue;
        let colorClass = colorValue?.startsWith("#")
          ? `${key}-[${colorValue}]`
          : colorValue || "";
        if (colorClass && !classList.includes(colorClass)) {
          pickTailwindClassForProperty({
            option: { tailwind: colorClass, css: "" },
            propertyType:
              key === "from"
                ? "FROM_COLOR"
                : key === "to"
                ? "TO_COLOR"
                : "VIA_COLOR",
            setTemporaryDraggingValue: undefined,
            variant: undefined,
          });
        }
        setValue({
          tailwind: stopClass,
          css: `--tw-gradient-${key}-position: ${value}`,
        });
        setGradient((prev) => {
          let color = prev[key]?.color;
          if (!color || !color.value) {
            color = {
              value: colorValue,
              classname: colorClass,
            };
          }
          return {
            ...prev,
            [key]: {
              ...prev[key],
              color,
              position: {
                value: value,
                classname: stopClass,
              },
              set: true,
            },
            direction: prev.direction,
          };
        });
      }
    },
    [
      setGradient,
      setValue,
      title,
      isDragging,
      fromColorValue,
      toColorValue,
      viaColorValue,
      classList,
      pickTailwindClassForProperty,
    ]
  );

  const handlePositionRangeMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handlePositionRangeMouseUp = useCallback(() => {
    setIsDragging(false);
    const key = title.toLowerCase();
    const arbitraryClass = buildPositionArbitraryClass(
      key,
      `${positionValueForInput.toString()}%`
    );
    setValue(arbitraryClass);
  }, [positionValueForInput]);

  const buildPositionArbitraryClass = (key: string, input: string) => {
    let css = `--tw-gradient-from-position: ${input}`;
    let tailwind = `from-[${input}]`;

    if (key === "to") {
      css = `--tw-gradient-to-position: ${input}`;
      tailwind = `to-[${input}]`;
    }

    if (key === "via") {
      css = `--tw-gradient-via-position: ${input}`;
      tailwind = `via-[${input}]`;
    }

    return {
      tailwind: tailwind,
      css: css,
    };
  };

  const buildColorPreview = (option: ComboBoxOptionType): ReactNode => {
    const match = option.tailwind?.match(
      /((?:from|to|via)-[a-z0-9-]+)(?:\/(\d{1,3}))?/
    );
    const baseClass = match?.[1] || "";
    const opacity = match?.[2] ? parseInt(match[2], 10) / 100 : 1;

    let color = "#000000";

    const arbitraryMatch =
      option.tailwind.match(/\\!?\[#([0-9a-fA-F]{6,8})\]\\/i) ||
      option.tailwind.match(/\\\[!?#[0-9a-fA-F]{6,8}\\\]/i);
    if (arbitraryMatch) {
      const hexMatch = arbitraryMatch[0].match(/#([0-9a-fA-F]{6,8})/);
      if (hexMatch) color = `#${hexMatch[1]}`;
    } else {
      const prop = PROPERTIES.find((p) => p.tailwind === baseClass);
      if (prop?.css) {
        const hexMatch = prop.css.match(/#([0-9a-fA-F]{6})/);
        if (hexMatch) {
          color = `#${hexMatch[1]}`;
        } else {
          const varMatch = prop.css.match(/var\((--[a-zA-Z0-9-_]+)\)/);
          if (varMatch) {
            const cssVar = varMatch[1];
            let cssVarValue = "";
            if (typeof window !== "undefined") {
              cssVarValue = getComputedStyle(document.documentElement)
                .getPropertyValue(cssVar)
                .trim();
            }
            if (cssVarValue) color = cssVarValue;
          }
        }
      }
    }

    if (!color) color = "#000000";

    let previewColor = color;
    if (color.startsWith("#")) {
      const { r, g, b } = hexToRgba(color);
      previewColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } else if (color.startsWith("rgb")) {
      if (opacity !== 1) {
        previewColor = color.replace(
          /rgb\(([^)]+)\)/,
          (m, rgbVals) => `rgba(${rgbVals}, ${opacity})`
        );
      } else {
        previewColor = color;
      }
    } else {
      previewColor = color;
    }

    return (
      <div
        className="w-full h-full border border-secondary-n-950 rounded-sm"
        style={{
          backgroundColor: previewColor,
        }}
      />
    );
  };

  return (
    <div className="col-span-14">
      <div className="grid grid-cols-14 items-start">
        <div className="col-span-5">
          <div className="text-xs text-secondary-800 font-medium">{title}</div>
        </div>
        <div className="col-span-9">
          <div className="grid grid-cols-10 gap-1 items-center">
            <div className="col-span-10">
              <div ref={colorInputRef}>
                <div
                  className="flex justify-between items-center mb-2"
                  data-tooltip-id="tooltip"
                  data-tooltip-content={`${title} color`}
                  data-tooltip-place="bottom"
                  data-tooltip-offset={5}
                  data-tooltip-hidden={Boolean(comboboxOpen || dropdownVisible)}
                  onMouseDown={(event) => {
                    if (event.button === 2) {
                      event.preventDefault();
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (
                      enhancedDropdownOptions &&
                      enhancedDropdownOptions.length > 0
                    ) {
                      setDropdownVisible(true);
                    }
                  }}
                >
                  <div
                    className={cn(
                      "flex items-center gap-1 w-full max-w-[140px] h-7 bg-secondary-n-950 rounded-md relative",
                      focused && "border border-primary-400"
                    )}
                  >
                    <div className="ml-1 absolute">
                      <ColorSwatch
                        color={cleanGradientVariables(
                          cleanedColorValue
                        ).replace("/ 0)", "/ 1)")}
                        size="sm"
                        showBorder={false}
                      />
                    </div>
                    <input
                      ref={inputRef}
                      disabled={disabled}
                      value={cleanedColorValue}
                      onChange={onInputChange}
                      onFocus={(event) => {
                        event.target.select();
                        setComboboxOpen(true);
                        setFocused(true);

                        if (onInputFocus) {
                          onInputFocus(event);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      onBlur={() => {
                        setFocused(false);
                      }}
                      className={`text-2xs px-5 border-none outline-none font-medium w-full h-full rounded-md bg-secondary-n-950 ${
                        disabled || value?.set === false
                          ? "text-secondary-600"
                          : "text-white"
                      }`}
                    />

                    <button
                      type="button"
                      onClick={
                        onOpenVariantModal
                          ? onOpenVariantModal
                          : () => setDropdownVisible(true)
                      }
                      className="absolute right-1"
                      ref={variantAnchorRef}
                    >
                      <ChevronDown className="text-secondary-n-500 h-4 w-4" />
                    </button>

                    <DropdownInput
                      className="w-40"
                      dropdownOptions={enhancedDropdownOptions}
                      onDropdownSelected={(option, e) => {
                        e?.stopPropagation();
                        handleDropdownOptionClick(option);
                      }}
                      visible={Boolean(dropdownVisible)}
                      setVisible={setDropdownVisible}
                    />
                  </div>
                  {comboboxOpen && colorProperties && (
                    <ComboBox
                      input={cleanedColorValue}
                      options={colorProperties}
                      open={comboboxOpen}
                      closeCombobox={() => setComboboxOpen(false)}
                      onOptionSelected={(option: ComboBoxOptionType, e) => {
                        e?.stopPropagation();
                        handleComboboxOptionClick(option);
                      }}
                      buildOptionPreview={buildColorPreview}
                      buildArbitraryClass={buildArbitraryClass}
                      anchorRef={colorInputRef}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <DraggableInput
                propertyState={{
                  propertyValue: `${positionValueForInput}%`,
                  displayValue: `${positionValueForInput}%`,
                  isSet: true,
                  valueByVariant: positionState?.valueByVariant || {},
                }}
                labelContent={null}
                comboboxOptions={positionProperties}
                onComboboxSelected={(option, variant) => {
                  if (title === "From") {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: "FROM_POSITION",
                      setTemporaryDraggingValue: undefined,
                      variant,
                    });
                  } else if (title === "Via") {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: "VIA_POSITION",
                      setTemporaryDraggingValue: undefined,
                      variant,
                    });
                  } else if (title === "To") {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: "TO_POSITION",
                      setTemporaryDraggingValue: undefined,
                      variant,
                    });
                  }
                  const key = title.toLowerCase();
                  const positionValue = extractPositionValue(option);
                  setGradient((prev) => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      position: {
                        value: positionValue,
                        classname: `${key}-[${positionValue}]`,
                      },
                      set: true,
                    },
                    direction: prev.direction,
                  }));
                }}
                onSliderValueUpdate={(option, variant) => {
                  if (title === "From") {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: "FROM_POSITION",
                      setTemporaryDraggingValue: undefined,
                      variant,
                    });
                  } else if (title === "Via") {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: "VIA_POSITION",
                      setTemporaryDraggingValue: undefined,
                      variant,
                    });
                  } else if (title === "To") {
                    pickTailwindClassForProperty({
                      option,
                      propertyType: "TO_POSITION",
                      setTemporaryDraggingValue: undefined,
                      variant,
                    });
                  }
                  const key = title.toLowerCase();
                  const positionValue = extractPositionValue(option);
                  setGradient((prev) => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      position: {
                        value: positionValue,
                        classname: `${key}-[${positionValue}]`,
                      },
                      set: true,
                    },
                    direction: prev.direction,
                  }));
                }}
                buildArbitraryClass={(input: string) => {
                  return buildPositionArbitraryClass(
                    title.toLowerCase(),
                    input
                  );
                }}
                min={0}
                max={100}
                step={1}
                fallback={{ number: 100, unit: "%" }}
                tooltipContent={`${title} stop position`}
                onClear={() => {
                  if (value?.position?.classname) {
                    removeTailwindClass(value?.position?.classname);
                  }
                }}
              />
            </div>
            <div className="col-span-4">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={positionValueForInput / 100}
                onChange={handlePositionRangeChange}
                onMouseDown={handlePositionRangeMouseDown}
                onMouseUp={handlePositionRangeMouseUp}
                onTouchStart={handlePositionRangeMouseDown}
                onTouchEnd={handlePositionRangeMouseUp}
                className={`w-full appearance-none bg-transparent
                      [&::-webkit-slider-runnable-track]:w-full
                      [&::-webkit-slider-runnable-track]:h-3
                      [&::-webkit-slider-runnable-track]:bg-primary-0
                      [&::-webkit-slider-runnable-track]:rounded-full
                      [&::-webkit-slider-thumb]:w-3
                      [&::-webkit-slider-thumb]:h-3
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:bg-primary-n-50
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:transition-all
                      [&::-webkit-slider-thumb]:duration-150
                      [&::-webkit-slider-thumb]:ease-in-out
                      [&::-moz-range-thumb]:w-3
                      [&::-moz-range-thumb]:h-3
                      [&::-moz-range-thumb]:appearance-none
                      [&::-moz-range-thumb]:bg-primary-n-50
                      [&::-moz-range-thumb]:border-0
                      [&::-moz-range-thumb]:border-transparent
                      [&::-moz-range-thumb]:rounded-full
                      [&::-moz-range-thumb]:transition-all
                      [&::-moz-range-thumb]:duration-150
                      [&::-moz-range-thumb]:ease-in-out
                      [&::-moz-range-track]:w-full
                      [&::-moz-range-track]:h-3
                      [&::-moz-range-track]:bg-primary-0
                      [&::-moz-range-track]:rounded-full
                    `}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GradientInputMemo = React.memo(GradientInput);

export default React.memo(CustomGradientInput);
